import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import { isValidCssMeasurement } from '../../utils/ids-number-utils/ids-number-utils';
import { getPercentageTextHtml, getInnerIndicatorHtml } from './ids-loading-indicator-attributes';

import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsElement from '../../core/ids-element';

import styles from './ids-loading-indicator.scss';
import type IdsOverlay from '../ids-modal/ids-overlay';
import '../ids-modal/ids-overlay';

const Base = IdsEventsMixin(
  IdsElement
);

/** CSS custom property for horizontal offset */
const CSS_OFFSET_X = '--ids-loading-indicator-offset-x';

/** CSS custom property for vertical offset */
const CSS_OFFSET_Y = '--ids-loading-indicator-offset-y';

/**
 * IDS Loading Indicator Component
 * @type {IdsLoadingIndicator}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @part container - the loader svg container element
 * @part progress - the percentage complete or active part of indeterminate section
 * @part circle - the "circle" area, which includes percentage and what 100% would cover on
 * indeterminate
 * @part percentage-text - the percentage text shown (when flag is set)
 */
@customElement('ids-loading-indicator')
@scss(styles)
export default class IdsLoadingIndicator extends Base {
  constructor() {
    super();
  }

  /**
   * Invoked each time the custom element is appended into a document-connected element.
   */
  connectedCallback() {
    super.connectedCallback();
    this.#updateProgress();
    this.#updatePlacement();
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes(): Array<string> {
    return [
      attributes.ALIGN,
      attributes.CONTAINED,
      attributes.GENERATIVE_AI,
      attributes.INLINE,
      attributes.LINEAR,
      attributes.OPACITY,
      attributes.OVERLAY,
      attributes.PERCENTAGE_VISIBLE,
      attributes.PROGRESS,
      attributes.STICKY,
      attributes.STOPPED,
      attributes.TYPE,
      attributes.VISIBLE,
      attributes.X_OFFSET,
      attributes.Y_OFFSET,
    ];
  }

  /**
   * Return the Template for the contents
   * @returns {string} The template
   */
  template(): string {
    let type = 'circular';

    if (this.hasAttribute(attributes.STICKY)) {
      type = 'sticky';
    }

    if (this.hasAttribute(attributes.LINEAR)) {
      type = 'linear';
    }

    if (this.hasAttribute(attributes.GENERATIVE_AI)) {
      type = 'generative-ai';
    }

    if (this.hasAttribute(attributes.CONTAINED)) {
      type = 'contained';
    }

    return getInnerIndicatorHtml({
      progress: this.progress,
      percentageVisible: this.percentageVisible,
      inline: this.inline,
      type,
      slotted: this.classList.contains('slot-loading-indicator'),
    });
  }

  /**
   * @returns {boolean | string} value Flag indicating whether or not this component
   * will be nested as a sub-part of another component (e.g. input);
   * renders a smaller variant.
   */
  get inline() {
    return this.hasAttribute(attributes.INLINE);
  }

  /**
   * @param {boolean | string} value Flag indicating whether or not this component
   * will be nested as a sub-part of another component (e.g. input);
   * renders a smaller variant.
   */
  set inline(value: boolean | string) {
    const isTruthy = stringToBool(value);

    if (isTruthy) {
      this.setAttribute(attributes.INLINE, '');
    }

    if (!isTruthy) {
      this.removeAttribute(attributes.INLINE);
    }
  }

  /**
   * @returns {string | null} the horizontal (x) position offset for the loading indicator
   */
  get xOffset(): string | null {
    return this.getAttribute(attributes.X_OFFSET);
  }

  /**
   * @param {string | null} value the horizontal (x) position for the loading indicator
   * (e.g. '30%', '100px')
   */
  set xOffset(value: string | null) {
    if (value && isValidCssMeasurement(value)) {
      this.setAttribute(attributes.X_OFFSET, value);
    } else {
      this.removeAttribute(attributes.X_OFFSET);
    }
    this.#updatePlacement();
  }

  /**
   * @returns {string | null} the vertical (y) position offset for the loading indicator
   */
  get yOffset(): string | null {
    return this.getAttribute(attributes.Y_OFFSET);
  }

  /**
   * @param {string | null} value the vertical (y) position for the loading indicator
   * (e.g. '30%', '100px')
   */
  set yOffset(value: string | null) {
    if (value && isValidCssMeasurement(value)) {
      this.setAttribute(attributes.Y_OFFSET, value);
    } else {
      this.removeAttribute(attributes.Y_OFFSET);
    }
    this.#updatePlacement();
  }

  /**
   * Update placement CSS custom properties based on offset attributes
   * @private
   */
  #updatePlacement(): void {
    if (this.xOffset) {
      this.style.setProperty(CSS_OFFSET_X, this.xOffset);
    } else {
      this.style.removeProperty(CSS_OFFSET_X);
    }

    if (this.yOffset) {
      this.style.setProperty(CSS_OFFSET_Y, this.yOffset);
    } else {
      this.style.removeProperty(CSS_OFFSET_Y);
    }
  }

  /**
   * @returns {number|undefined} the percentage completed for the indicator;
   * if not specified, the indicator is set into indeterminate mode (e.g. no specific
   * progress with an animation)
   */
  get progress() {
    const value = this.getAttribute(attributes.PROGRESS);

    return value !== null ? parseInt(value) : undefined;
  }

  /**
   * @param {number|undefined} value Represents the percentage completed for the indicator;
   * if not specified, the indicator is set into indeterminate mode (e.g. no specific
   * progress with an animation)
   */
  set progress(value: any) {
    const hasValue = !Number.isNaN(Number.parseFloat(value));
    if (hasValue) {
      this.setAttribute(attributes.PROGRESS, String(parseFloat(value)));

      this.shadowRoot?.querySelector('svg')?.classList.remove('indeterminate');
      this.shadowRoot?.querySelector('svg')?.classList.add('determinate');
    } else {
      this.removeAttribute(attributes.PROGRESS);
    }

    this.#updateProgress();
  }

  /**
   * Update current animation progress
   * @private
   */
  #updateProgress() {
    if (this.progress !== undefined && !Number.isNaN(this.progress)) {
      this.shadowRoot?.querySelector('svg')?.style.setProperty('--progress', this.type === 'circular' ? `${this.progress}px` : this.progress);
    } else {
      this.shadowRoot?.querySelector('svg')?.style.removeProperty('--progress');
    }

    this.#updatePercentageVisible();
  }

  /**
   * type; causes the indicator to stick to the top of the innermost IdsElement parent
   * and span it horizontally. If set, will unflag this indicator with any other
   * flag types set.
   * @param {boolean | string} value Flags the indicator as being an sticky indicator
   */
  set sticky(value: boolean | string) {
    this.#onUpdateTypeFlag(attributes.STICKY, stringToBool(value));
  }

  /**
   * @returns {boolean|string} value Flags the indicator as being an sticky indicator
   * type; causes the indicator to stick to the top of the innermost IdsElement parent
   * and span it horizontally. If set, will unflag this indicator with any other
   * flag types set.
   */
  get sticky() {
    return this.hasAttribute(attributes.STICKY);
  }

  /**
   * Flags the indicator as being an contained indicator relative to nearest parent
   * @param {boolean | string} value true if the indicator should be contained
   */
  set contained(value: boolean | string) {
    this.#onUpdateTypeFlag(attributes.CONTAINED, stringToBool(value));
  }

  /**
   * @returns {boolean | string} Flags the indicator as being a
   * contained indicator relative to nearest parent
   */
  get contained() {
    return this.hasAttribute(attributes.CONTAINED);
  }

  /**
   * @param {boolean} value Whether the percentage text should be visible
   * (not applicable to `sticky` loading indicators).
   */
  set percentageVisible(value: boolean) {
    const isTruthy = stringToBool(value);

    if (isTruthy && !this.hasAttribute(attributes.PERCENTAGE_VISIBLE)) {
      this.setAttribute(attributes.PERCENTAGE_VISIBLE, '');
    } else if (!isTruthy && this.hasAttribute(attributes.PERCENTAGE_VISIBLE)) {
      this.removeAttribute(attributes.PERCENTAGE_VISIBLE);
    }

    this.#updatePercentageVisible();
  }

  /**
   * @returns {boolean} Whether the percentage text should be visible
   * (not applicable to `sticky` loading indicators).
   */
  get percentageVisible() {
    return this.hasAttribute(attributes.PERCENTAGE_VISIBLE);
  }

  /**
   * Set the amount visible
   */
  #updatePercentageVisible(): void {
    const percentageEl = this.shadowRoot?.querySelector('[part="percentage-text"]');

    if (percentageEl) {
      percentageEl.remove();
    }

    if (this.percentageVisible && (this.type !== 'sticky')) {
      const template = document.createElement('template');
      template.innerHTML = getPercentageTextHtml({ type: this.type, progress: this.progress });
      this.shadowRoot?.appendChild(template.content.cloneNode(true));
    }
  }

  /**
   * Get the current overlay setting
   * @returns {boolean} true if an overlay is used
   */
  get overlay(): boolean {
    return stringToBool(this.getAttribute(attributes.OVERLAY));
  }

  /**
   * Set the overlay state between visible and not visible
   * @param {boolean} val can be set to true to show the overlay
   */
  set overlay(val) {
    const booleanValue = stringToBool(val);
    if (booleanValue) this.setAttribute(attributes.OVERLAY, '');
    else this.removeAttribute(attributes.OVERLAY);
    this.#refreshOverlay(booleanValue);
  }

  /**
   * Set the overlay state between visible and not visible
   * @param {boolean} visible can be set to true to show the overlay
   */
  #refreshOverlay(visible: boolean) {
    const overlay = this.shadowRoot?.querySelector<IdsOverlay>('ids-overlay');
    if (visible) {
      overlay?.setAttribute('visible', 'true');
    } else {
      overlay?.removeAttribute('visible');
    }
  }

  /**
   * @returns {string} the opacity value for the overlay
   */
  get opacity(): string {
    return this.getAttribute(attributes.OPACITY) || '0.7';
  }

  /**
   * @param {string} value the opacity value for the overlay (e.g. '0.5', '0.8')
   */
  set opacity(value: string) {
    if (value) {
      this.setAttribute(attributes.OPACITY, value);
    } else {
      this.removeAttribute(attributes.OPACITY);
    }
    const overlay = this.shadowRoot?.querySelector<IdsOverlay>('ids-overlay');
    if (overlay) overlay.opacity = value;
  }

  /**
   * Stop and hide the loading indicator
   */
  stop(): void {
    this.triggerEvent('stopped.loading-indicator', this, {
      detail: {
        elem: this,
        loading: false,
      },
      bubbles: true,
      cancelable: false,
      composed: true
    });
    this.setAttribute('stopped', '');
  }

  /**
   * Re start the loading indicator
   */
  start(): void {
    this.triggerEvent('started.loading-indicator', this, {
      detail: {
        elem: this,
        loading: true,
      },
      bubbles: true,
      cancelable: false,
      composed: true
    });
    this.removeAttribute('stopped');
  }

  /**
   * causes the indicator to span its parent component horizontally and
   * be represented as a horizontal/linear bar. If set, removes current
   * flag types that may be set.
   * @param {boolean | string} value Flags the indicator as a linear indicator type;
   */
  set linear(value: boolean | string) {
    this.#onUpdateTypeFlag(attributes.LINEAR, stringToBool(value));
  }

  /**
   * causes the indicator to span its parent component horizontally and
   * be represented as a horizontal/linear bar. If set, removes current
   * flag types that may be set.
   * @returns {boolean | string} value Flags the indicator as a linear indicator type;
   */
  get linear() {
    return this.hasAttribute(attributes.LINEAR);
  }

  /**
   * Set the animation to Gen AI
   * @param {boolean} value can be center to center to center align the loader
   */
  set generativeAi(value: boolean) {
    const val = stringToBool(value);
    if (val) {
      this.shadowRoot?.querySelector('.ai-loading-indicator')?.remove();
      this.setAttribute(attributes.GENERATIVE_AI, '');
      this.render(true);
      if (this.shadowRoot?.querySelectorAll('.ai-loading-indicator')?.length === 2) this.shadowRoot?.querySelector('.ai-loading-indicator')?.remove();
    } else {
      this.shadowRoot?.querySelector('.ai-loading-indicator')?.remove();
      this.removeAttribute(attributes.GENERATIVE_AI);
      this.render(true);
    }
  }

  get generativeAi(): boolean {
    return stringToBool(this.getAttribute(attributes.GENERATIVE_AI));
  }

  /**
   * Set the alignment between normal and center
   * @param {string} value can be center to center to center align the loader
   */
  set align(value: string) {
    if (value === 'center') {
      this.setAttribute(attributes.ALIGN, 'center');
    } else {
      this.removeAttribute(attributes.ALIGN);
    }
  }

  get align() {
    return this.getAttribute(attributes.ALIGN) || 'normal';
  }

  /**
   * @returns {'circular'|'linear'|'sticky'} type The type of loading indicator
   */
  get type() {
    return this.#type || 'circular';
  }

  /**
   * type-flag set based on attributes
   * @type {'circular'|'linear'|'sticky'}
   */
  #type: string | undefined;

  /**
   * updates type based on attribute setter passed
   * @param {*} attribute attribute of flag set
   * @param {*} value value of attribute passed to flag
   */
  #onUpdateTypeFlag(attribute: string, value: boolean | string): void {
    const isTruthy = stringToBool(value);

    if (isTruthy) {
      if (this.#type !== attribute) {
        if (this.#type && this.hasAttribute(this.#type)) {
          this.removeAttribute(this.#type);
        }

        this.#type = attribute;
        if (!this.hasAttribute(attribute)) {
          this.setAttribute(attribute, '');
        }
        this.render(true);
      }

      if (!this.hasAttribute(attribute)) {
        this.setAttribute(attribute, '');
      }
    }

    if (!isTruthy) {
      if (this.hasAttribute(attribute)) {
        this.removeAttribute(attribute);
      }

      if (this.#type === attribute) {
        this.#type = undefined;
        this.render(true);
      }
    }
  }
}
